// Lightweight IP geolocation via ipapi.co. Used to personalize the marketing
// hero ("Hey Guiri from Berlin 🇩🇪") and to suggest a locale on first visit.
// Cached in localStorage with a 24-hour TTL so one call per device is enough;
// silent on any failure.

export interface Geo {
  city: string | null;
  countryCode: string | null;
  countryName: string | null;
  language: string | null;
}

const CACHE_KEY = "guiri.geo";
const CACHE_TIMESTAMP_KEY = "guiri.geo_ts";
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function readCache(): Geo | null {
  if (typeof window === "undefined") return null;
  try {
    const ts = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!ts) return null;
    const age = Date.now() - parseInt(ts, 10);
    if (Number.isNaN(age) || age < 0 || age > TTL_MS) return null;
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Geo;
  } catch {
    // Storage disabled or quota — fall through to network.
    return null;
  }
}

function writeCache(geo: Geo): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(geo));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // Ignore quota / disabled storage.
  }
}

export async function fetchGeo(): Promise<Geo | null> {
  const cached = readCache();
  if (cached) return cached;

  try {
    const r = await fetch("https://ipapi.co/json/", { credentials: "omit" });
    if (!r.ok) return null;
    const d = await r.json();
    const geo: Geo = {
      city: typeof d.city === "string" && d.city ? d.city : null,
      countryCode: typeof d.country_code === "string" ? d.country_code : null,
      countryName: typeof d.country_name === "string" ? d.country_name : null,
      language:
        typeof d.languages === "string"
          ? (d.languages.split(",")[0] || "").split("-")[0].toLowerCase() || null
          : null,
    };
    writeCache(geo);
    return geo;
  } catch {
    return null;
  }
}

// ISO 3166-1 alpha-2 → 🇩🇪 regional-indicator emoji.
export const flagFromCC = (cc: string | null | undefined): string => {
  if (!cc || cc.length !== 2) return "";
  const up = cc.toUpperCase();
  if (!/^[A-Z]{2}$/.test(up)) return "";
  return String.fromCodePoint(
    0x1f1e6 + up.charCodeAt(0) - 65,
    0x1f1e6 + up.charCodeAt(1) - 65,
  );
};
